import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import {
  Check,
  Clock3,
  LogIn,
  Send,
  UserRoundMinus,
  UserRoundPlus,
  UsersRound,
  X,
} from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useSegmentedIndicator } from "../../hooks/useSegmentedIndicator";
import { socialErrorMessage, type SocialClient } from "../../hooks/useSocial";
import type { FriendSummary, PresenceStatus } from "../../types/protocol";
import { Avatar } from "../ui/Avatar";
import { FriendSearch } from "./FriendSearch";

export type FriendsTab = "friends" | "requests" | "add";

const tabLabels: Record<FriendsTab, string> = {
  friends: "Amis",
  requests: "Demandes",
  add: "Ajouter",
};

const presenceLabels: Record<PresenceStatus, string> = {
  offline: "Hors ligne",
  online: "En ligne",
  "in-lobby": "Dans un salon",
  "in-game": "En partie",
};

interface FriendsPanelProps {
  open: boolean;
  tab: FriendsTab;
  social: SocialClient;
  /** Vrai uniquement pour l'hôte d'un salon qui attend encore des joueurs. */
  canInvite: boolean;
  onTabChange: (tab: FriendsTab) => void;
  onClose: () => void;
  onJoinFriend: (roomCode: string) => void;
}

interface FriendRowProps {
  friend: FriendSummary;
  canInvite: boolean;
  invited: boolean;
  pending: string | null;
  onInvite: (friend: FriendSummary) => void;
  onJoin: (roomCode: string) => void;
  onRemove: (friend: FriendSummary) => void;
}

const FriendRow = ({
  friend,
  canInvite,
  invited,
  pending,
  onInvite,
  onJoin,
  onRemove,
}: FriendRowProps) => {
  const [confirming, setConfirming] = useState(false);
  const online = friend.status !== "offline";
  const removing = pending === `remove:${friend.userId}`;
  const inviting = pending === `invite:${friend.userId}`;
  const joinableCode = friend.joinable ? friend.roomCode : null;

  return (
    <motion.li
      className={`friend-row${online ? " is-online" : ""}${confirming ? " is-confirming" : ""}`}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18 }}
    >
      <Avatar profile={friend} size="md" showPresence status={friend.status} />
      <span className="friend-row__identity">
        <strong>{friend.displayName}</strong>
        <small>
          <i className={`presence-dot is-${friend.status}`} aria-hidden="true" />
          {presenceLabels[friend.status]}
        </small>
      </span>

      {confirming ? (
        <span className="friend-row__actions friend-row__actions--confirm">
          <span className="friend-row__note">Retirer&nbsp;?</span>
          <button
            className="secondary-button secondary-button--danger"
            type="button"
            disabled={removing}
            onClick={() => onRemove(friend)}
            aria-label={`Confirmer le retrait de ${friend.handle}`}
          >
            {removing ? <span className="button-spinner" aria-hidden="true" /> : <Check size={15} />}
            Retirer
          </button>
          {/* `--compact` réduirait ce bouton à un carré de 40 px sur mobile :
              réservé aux boutons à icône, il écraserait ici le seul libellé. */}
          <button className="ghost-button" type="button" onClick={() => setConfirming(false)}>
            Annuler
          </button>
        </span>
      ) : (
        // Sans bouton texte il ne reste qu'une icône : elle tient sur la ligne
        // du nom et n'a pas à s'offrir une seconde ligne sur mobile.
        <span
          className={`friend-row__actions${joinableCode !== null || canInvite ? " friend-row__actions--wide" : ""}`}
        >
          {joinableCode !== null && (
            <button
              className="secondary-button secondary-button--compact"
              type="button"
              onClick={() => onJoin(joinableCode)}
              aria-label={`Rejoindre la table de ${friend.displayName}`}
            >
              <LogIn size={15} /> Rejoindre
            </button>
          )}
          {canInvite && (
            invited ? (
              <span className="friend-row__note"><Check size={13} /> Invité</span>
            ) : (
              <button
                className="primary-button primary-button--small"
                type="button"
                disabled={!online || inviting}
                onClick={() => onInvite(friend)}
                aria-label={`Inviter ${friend.displayName} à ta table`}
              >
                {inviting ? (
                  <span className="button-spinner" aria-hidden="true" />
                ) : (
                  <Send size={15} />
                )}
                Inviter
              </button>
            )
          )}
          <button
            className="icon-button"
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={`Retirer ${friend.handle} de tes amis`}
            title="Retirer des amis"
          >
            <UserRoundMinus size={17} />
          </button>
        </span>
      )}
    </motion.li>
  );
};

export const FriendsPanel = ({
  open,
  tab,
  social,
  canInvite,
  onTabChange,
  onClose,
  onJoinFriend,
}: FriendsPanelProps) => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const resetInvited = social.resetInvited;

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  // Quitter la table annule les invitations côté serveur sans nous prévenir.
  useEffect(() => {
    if (!canInvite) resetInvited();
  }, [canInvite, resetInvited]);

  /**
   * Sous 640px le panneau devient une feuille ancrée en bas, glissable au doigt.
   * Au-dessus il reste une carte latérale, qu'on ne fait pas glisser.
   */
  const isSheet = useMediaQuery("(max-width: 640px)");
  const tabIndicator = useSegmentedIndicator<HTMLDivElement>(
    ["friends", "requests", "add"].indexOf(tab),
  );
  const dragControls = useDragControls();

  const invite = async (friend: FriendSummary) => {
    await social.sendInvite(friend.userId);
  };

  const remove = async (friend: FriendSummary) => {
    await social.removeFriend(friend.userId);
  };

  const failure = socialErrorMessage(social.error);
  const incomingCount = social.incoming.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="social-drawer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && event.button === 0) onClose();
          }}
        >
          <motion.aside
            className="social-panel glass-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            // La feuille monte du bas ; la carte de bureau arrive par la droite.
            // Animer les deux axes à la fois donnait une diagonale que personne
            // n'associe à un tiroir.
            initial={isSheet ? { y: "100%" } : { opacity: 0, x: 34 }}
            animate={isSheet ? { y: 0 } : { opacity: 1, x: 0 }}
            exit={isSheet ? { y: "100%" } : { opacity: 0, x: 24 }}
            transition={{ type: "spring", stiffness: 320, damping: 34, mass: 0.9 }}
            drag={isSheet ? "y" : false}
            // Le geste ne part que de la poignée : sinon le défilement de la
            // liste emporterait la feuille avec lui.
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.55 }}
            onDragEnd={(_event, info) => {
              // On ferme sur la distance OU sur l'élan : un geste bref et vif
              // doit suffire, comme sur une feuille native.
              if (info.offset.y > 120 || info.velocity.y > 700) onClose();
            }}
          >
            {isSheet && (
              <span
                className="social-panel__grabber"
                aria-hidden="true"
                onPointerDown={(event) => dragControls.start(event)}
              />
            )}
            <header className="social-panel__head">
              <div>
                <span className="section-kicker"><UsersRound size={14} /> Entre amis</span>
                <h2 id={titleId}>Tes amis</h2>
              </div>
              <button
                ref={closeRef}
                className="icon-button icon-button--surface"
                type="button"
                onClick={onClose}
                aria-label="Fermer le panneau des amis"
              >
                <X size={18} />
              </button>
            </header>

            <div
              className="social-tabs"
              ref={tabIndicator.containerRef}
              role="tablist"
              aria-label="Sections des amis"
            >
              {tabIndicator.indicator !== null && (
                <motion.span
                  className="segmented-control__active"
                  animate={{
                    x: tabIndicator.indicator.box.x,
                    y: tabIndicator.indicator.box.y,
                    width: tabIndicator.indicator.box.width,
                    height: tabIndicator.indicator.box.height,
                  }}
                  transition={
                    tabIndicator.indicator.animate
                      ? { type: "spring", stiffness: 420, damping: 36 }
                      : { duration: 0 }
                  }
                />
              )}
              {(["friends", "requests", "add"] as const).map((item) => (
                <button
                  type="button"
                  role="tab"
                  key={item}
                  aria-selected={tab === item}
                  className={tab === item ? "is-active" : ""}
                  onClick={() => onTabChange(item)}
                >
                  <span>{tabLabels[item]}</span>
                  {item === "requests" && incomingCount > 0 && (
                    <span className="social-tabs__badge" aria-label={`${incomingCount} en attente`}>
                      {incomingCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="social-panel__body">
              {tab === "friends" && (
                <>
                  {failure && <p className="social-alert" role="alert">{failure}</p>}
                  {social.friends.length === 0 ? (
                    <div className="social-empty">
                      <span className="social-empty__icon" aria-hidden="true"><UsersRound size={20} /></span>
                      <strong>Ta liste est encore vide</strong>
                      <p>Ajoute les personnes avec qui tu joues souvent : tu verras qui est en ligne et tu pourras les inviter en un geste.</p>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => onTabChange("add")}
                      >
                        <UserRoundPlus size={16} /> Ajouter un ami
                      </button>
                    </div>
                  ) : (
                    <ul className="social-list">
                      <AnimatePresence initial={false}>
                        {social.friends.map((friend) => (
                          <FriendRow
                            key={friend.userId}
                            friend={friend}
                            canInvite={canInvite}
                            invited={social.invitedUserIds.includes(friend.userId)}
                            pending={social.pending}
                            onInvite={(target) => void invite(target)}
                            onJoin={(code) => onJoinFriend(code)}
                            onRemove={(target) => void remove(target)}
                          />
                        ))}
                      </AnimatePresence>
                    </ul>
                  )}
                  {!canInvite && social.friends.length > 0 && (
                    <p className="social-hint">
                      Crée une table pour pouvoir inviter tes amis d’un seul geste.
                    </p>
                  )}
                </>
              )}

              {tab === "requests" && (
                <>
                  {failure && <p className="social-alert" role="alert">{failure}</p>}
                  {social.incoming.length === 0 && social.outgoing.length === 0 ? (
                    <div className="social-empty">
                      <span className="social-empty__icon" aria-hidden="true"><Clock3 size={20} /></span>
                      <strong>Aucune demande en attente</strong>
                      <p>Les invitations d’amitié reçues et envoyées apparaîtront ici.</p>
                    </div>
                  ) : (
                    <>
                      {social.incoming.length > 0 && (
                        <section className="social-section" aria-label="Demandes reçues">
                          <h3>Reçues</h3>
                          <ul className="social-list">
                            <AnimatePresence initial={false}>
                              {social.incoming.map((request) => (
                                <motion.li
                                  className="friend-row"
                                  key={request.id}
                                  layout
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -6 }}
                                  transition={{ duration: 0.18 }}
                                >
                                  <Avatar profile={request.profile} size="md" />
                                  <span className="friend-row__identity">
                                    <strong>{request.profile.displayName}</strong>
                                    <small>#{request.profile.discriminator}</small>
                                  </span>
                                  <span className="friend-row__actions">
                                    <button
                                      className="primary-button primary-button--small"
                                      type="button"
                                      disabled={social.pending === `respond:${request.id}`}
                                      onClick={() => void social.respondToRequest(request.id, true)}
                                      aria-label={`Accepter la demande de ${request.profile.handle}`}
                                    >
                                      {social.pending === `respond:${request.id}` ? (
                                        <span className="button-spinner" aria-hidden="true" />
                                      ) : (
                                        <Check size={15} />
                                      )}
                                      Accepter
                                    </button>
                                    <button
                                      className="ghost-button ghost-button--compact"
                                      type="button"
                                      disabled={social.pending === `respond:${request.id}`}
                                      onClick={() => void social.respondToRequest(request.id, false)}
                                      aria-label={`Refuser la demande de ${request.profile.handle}`}
                                    >
                                      Refuser
                                    </button>
                                  </span>
                                </motion.li>
                              ))}
                            </AnimatePresence>
                          </ul>
                        </section>
                      )}

                      {social.outgoing.length > 0 && (
                        <section className="social-section" aria-label="Demandes envoyées">
                          <h3>Envoyées</h3>
                          <ul className="social-list">
                            <AnimatePresence initial={false}>
                              {social.outgoing.map((request) => (
                                <motion.li
                                  className="friend-row"
                                  key={request.id}
                                  layout
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -6 }}
                                  transition={{ duration: 0.18 }}
                                >
                                  <Avatar profile={request.profile} size="md" />
                                  <span className="friend-row__identity">
                                    <strong>{request.profile.displayName}</strong>
                                    <small><Clock3 size={12} /> En attente de réponse</small>
                                  </span>
                                  <span className="friend-row__actions">
                                    <button
                                      className="ghost-button ghost-button--compact"
                                      type="button"
                                      disabled={social.pending === `cancel:${request.id}`}
                                      onClick={() => void social.cancelRequest(request.id)}
                                      aria-label={`Annuler la demande envoyée à ${request.profile.handle}`}
                                    >
                                      {social.pending === `cancel:${request.id}` ? (
                                        <span className="button-spinner" aria-hidden="true" />
                                      ) : (
                                        <X size={15} />
                                      )}
                                      Annuler
                                    </button>
                                  </span>
                                </motion.li>
                              ))}
                            </AnimatePresence>
                          </ul>
                        </section>
                      )}
                    </>
                  )}
                </>
              )}

              {tab === "add" && <FriendSearch social={social} />}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
